import React, { useEffect, useState } from 'react';
import { adminSiteEventApi, uploadApi } from '../../api';
import { SiteEvent } from '../../types';
import Loading from '../../components/common/Loading';
import { toImageUrl } from '../../utils/image';

interface FormState {
  title: string;
  description: string;
  imageUrl: string | null;
  startAt: string;
  endAt: string;
  active: boolean;
  displayOrder: number;
}

const emptyForm: FormState = {
  title: '', description: '', imageUrl: null, startAt: '', endAt: '', active: true, displayOrder: 0,
};

const AdminSiteEvents: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<SiteEvent[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await adminSiteEventApi.getList();
    setEvents(r.data.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm, displayOrder: events.length });
    setShowForm(true);
  };

  const openEdit = (e: SiteEvent) => {
    setEditId(e.id);
    setForm({
      title: e.title,
      description: e.description ?? '',
      imageUrl: e.imageUrl,
      startAt: e.startAt ? e.startAt.slice(0, 16) : '',
      endAt: e.endAt ? e.endAt.slice(0, 16) : '',
      active: e.active,
      displayOrder: e.displayOrder,
    });
    setShowForm(true);
  };

  const handleUpload = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadApi.image(file);
      setForm(f => ({ ...f, imageUrl: res.data.data }));
    } catch (err: any) {
      alert(err.response?.data?.message || '이미지 업로드 실패');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { alert('제목을 입력해주세요.'); return; }
    if (!form.imageUrl) { alert('이벤트 이미지를 업로드해주세요.'); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      imageUrl: form.imageUrl,
      startAt: form.startAt ? form.startAt + ':00' : null,
      endAt: form.endAt ? form.endAt + ':00' : null,
      active: form.active,
      displayOrder: Number(form.displayOrder),
    };
    try {
      if (editId) {
        await adminSiteEventApi.update(editId, payload);
      } else {
        await adminSiteEventApi.create(payload);
      }
      setShowForm(false);
      await load();
    } catch (err: any) {
      alert(err.response?.data?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 이벤트를 삭제하시겠습니까?')) return;
    await adminSiteEventApi.delete(id);
    await load();
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>이벤트 관리</h2>
        <button style={styles.addBtn} onClick={openCreate}>+ 이벤트 추가</button>
      </div>

      {events.length === 0 ? (
        <div style={styles.empty}>
          <p>등록된 이벤트가 없습니다.</p>
          <button style={styles.addBtn} onClick={openCreate}>첫 이벤트 추가</button>
        </div>
      ) : (
        <div style={styles.list}>
          {events.map(e => (
            <div key={e.id} style={styles.card}>
              <div style={styles.thumbWrap}>
                <img src={toImageUrl(e.imageUrl)} alt="" style={styles.thumb} />
              </div>
              <div style={styles.cardInfo}>
                <p style={styles.cardTitle}>{e.title}</p>
                <div style={styles.cardRow}>
                  <span style={styles.orderBadge}>순서 {e.displayOrder}</span>
                  {e.active
                    ? <span style={styles.onBadge}>노출중</span>
                    : <span style={styles.offBadge}>숨김</span>}
                  {e.ended
                    ? <span style={styles.endedBadge}>종료</span>
                    : <span style={styles.ongoingBadge}>진행중</span>}
                </div>
                {(e.startAt || e.endAt) && (
                  <p style={styles.periodText}>
                    {e.startAt ? new Date(e.startAt).toLocaleDateString() : '상시'} ~ {e.endAt ? new Date(e.endAt).toLocaleDateString() : '상시'}
                  </p>
                )}
              </div>
              <div style={styles.cardBtns}>
                <button style={styles.editBtn} onClick={() => openEdit(e)}>수정</button>
                <button style={styles.delBtn} onClick={() => handleDelete(e.id)}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p style={styles.hint}>이벤트 목록(/events)에 노출중인 이벤트만 보이며, 종료일이 지나면 자동으로 "종료"로 표시됩니다. 첫구매 감사 쿠폰은 코드 고정 이벤트라 목록 최상단에 항상 표시됩니다.</p>

      {showForm && (
        <div style={styles.overlay}>
          <div style={styles.modal} onClick={ev => ev.stopPropagation()}>
            <h3 style={styles.modalTitle}>{editId ? '이벤트 수정' : '이벤트 추가'}</h3>

            <div style={styles.field}>
              <label style={styles.label}>이벤트 이미지 (배너)</label>
              <div style={styles.preview}>
                {form.imageUrl
                  ? <img src={toImageUrl(form.imageUrl)} alt="" style={styles.previewImg} />
                  : <div style={styles.previewEmpty}>이미지 없음</div>}
              </div>
              <label style={styles.uploadBtn}>
                {uploading ? '업로드 중...' : '이미지 선택'}
                <input type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
              </label>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>제목</label>
              <input style={styles.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>설명 (선택 — 예: 구매자 추첨 10명 안내)</label>
              <textarea rows={4} style={{ ...styles.input, resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>

            <div style={styles.row2}>
              <div style={styles.field}>
                <label style={styles.label}>시작일 (선택)</label>
                <input type="datetime-local" style={styles.input} value={form.startAt} onChange={e => setForm({ ...form, startAt: e.target.value })} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>종료일 (선택 — 비우면 상시)</label>
                <input type="datetime-local" style={styles.input} value={form.endAt} onChange={e => setForm({ ...form, endAt: e.target.value })} />
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>노출 순서 (작을수록 먼저)</label>
              <input type="number" style={styles.input} value={form.displayOrder} onChange={e => setForm({ ...form, displayOrder: Number(e.target.value) })} />
            </div>

            <label style={styles.checkboxRow}>
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
              <span>이벤트 노출 (체크 해제 시 목록에서 숨김)</span>
            </label>

            <div style={styles.modalBtns}>
              <button style={styles.cancelBtn} onClick={() => setShowForm(false)} disabled={saving}>취소</button>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700 },
  addBtn: { padding: '10px 20px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  empty: { padding: '60px 20px', textAlign: 'center', color: '#bbb', background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { display: 'flex', alignItems: 'center', gap: 16, background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', padding: 14 },
  thumbWrap: { width: 120, height: 80, borderRadius: 8, overflow: 'hidden', background: '#fafafa', flexShrink: 0 },
  thumb: { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' },
  cardInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#333', margin: 0 },
  cardRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  orderBadge: { fontSize: 12, color: '#666', background: '#f5f5f5', padding: '3px 10px', borderRadius: 10 },
  onBadge: { fontSize: 12, color: '#4caf50', background: '#e8f5e9', padding: '3px 10px', borderRadius: 10, fontWeight: 600 },
  offBadge: { fontSize: 12, color: '#999', background: '#f0f0f0', padding: '3px 10px', borderRadius: 10, fontWeight: 600 },
  ongoingBadge: { fontSize: 12, color: '#2e7d32', background: '#e8f5e9', padding: '3px 10px', borderRadius: 10, fontWeight: 600 },
  endedBadge: { fontSize: 12, color: '#999', background: '#eee', padding: '3px 10px', borderRadius: 10, fontWeight: 600 },
  periodText: { fontSize: 12, color: '#999', margin: 0 },
  cardBtns: { display: 'flex', gap: 6 },
  editBtn: { padding: '6px 14px', background: '#ece3f7', color: '#b39ddb', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  delBtn: { padding: '6px 14px', background: '#fff0f0', color: '#e53935', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  hint: { fontSize: 12, color: '#999', marginTop: 16 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 },
  modalTitle: { fontSize: 18, fontWeight: 700 },
  field: { display: 'flex', flexDirection: 'column', gap: 8, flex: 1 },
  row2: { display: 'flex', gap: 16 },
  label: { fontSize: 14, fontWeight: 700, color: '#333' },
  preview: { width: '100%', maxHeight: 280, background: '#fafafa', borderRadius: 10, border: '1px dashed #e0e0e0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  previewImg: { width: '100%', objectFit: 'contain', maxHeight: 280 },
  previewEmpty: { color: '#bbb', fontSize: 14, padding: 40 },
  uploadBtn: { alignSelf: 'flex-start', padding: '8px 18px', background: '#ece3f7', color: '#b39ddb', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  input: { padding: '11px 14px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#333', cursor: 'pointer' },
  modalBtns: { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 20px', border: '1px solid #e8e8e8', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 },
  saveBtn: { padding: '10px 24px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
};

export default AdminSiteEvents;
